import { Component, computed, Inject, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth.service';
import { CheckboxComponent } from 'src/app/shared/components/ui/checkbox/checkbox.component';
import { InputComponent } from 'src/app/shared/components/ui/input/input.component';
import { MaterialModule } from 'src/app/shared/material.module';

interface WorkspaceUser {
  uid: string;
  name: string;
  email: string;
  picture?: string;
}

@Component({
  selector: 'app-add-group-member',
  templateUrl: './add-group-member.component.html',
  styleUrls: ['./add-group-member.component.scss'],
  standalone: true,
  imports: [MaterialModule, TranslateModule, InputComponent, CheckboxComponent],
})
export class AddGroupMemberComponent implements OnInit {
  private authService = inject(AuthService);
  public dialogRef = inject(MatDialogRef<AddGroupMemberComponent>);

  // Signals
  users = signal<WorkspaceUser[]>([]);
  loading = signal(false);
  searchQuery = signal('');
  selectedUserIds = signal<Set<string>>(new Set());

  filteredUsers = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const allUsers = this.users();
    if (!query) return allUsers;
    return allUsers.filter((u) => u.name.toLowerCase().includes(query) || u.email.toLowerCase().includes(query));
  });

  constructor(@Inject(MAT_DIALOG_DATA) public data: { existingMemberIds: string[] }) {}

  ngOnInit() {
    this.loadWorkspaceUsers();
  }

  loadWorkspaceUsers() {
    this.loading.set(true);
    this.authService.getWorkspaceUsers(1000, 0, '', 'name', 'asc').subscribe({
      next: (res: any) => {
        const list = res.list || [];
        const existingIds = new Set(this.data?.existingMemberIds || []);

        const filtered = list
          .map((u: any) => ({
            uid: u.uid || u._id,
            name: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim(),
            email: u.email,
            picture: u.picture || null,
          }))
          .filter((u: any) => u.uid && !existingIds.has(u.uid));

        this.users.set(filtered);
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Failed to load workspace users:', err);
        this.loading.set(false);
      },
    });
  }

  onSearch(value: string) {
    this.searchQuery.set(value);
  }

  toggleUserSelection(uid: string) {
    const current = new Set(this.selectedUserIds());
    if (current.has(uid)) {
      current.delete(uid);
    } else {
      current.add(uid);
    }
    this.selectedUserIds.set(current);
  }

  isUserSelected(uid: string): boolean {
    return this.selectedUserIds().has(uid);
  }

  toggleSelectAll() {
    const currentFiltered = this.filteredUsers();
    const currentSelected = new Set(this.selectedUserIds());

    const allFilteredSelected = currentFiltered.every((u) => currentSelected.has(u.uid));

    if (allFilteredSelected) {
      currentFiltered.forEach((u) => currentSelected.delete(u.uid));
    } else {
      currentFiltered.forEach((u) => currentSelected.add(u.uid));
    }
    this.selectedUserIds.set(currentSelected);
  }

  isAllFilteredSelected(): boolean {
    const currentFiltered = this.filteredUsers();
    if (currentFiltered.length === 0) return false;
    return currentFiltered.every((u) => this.selectedUserIds().has(u.uid));
  }

  isSomeFilteredSelected(): boolean {
    const currentFiltered = this.filteredUsers();
    if (currentFiltered.length === 0) return false;
    const selected = currentFiltered.some((u) => this.selectedUserIds().has(u.uid));
    const all = this.isAllFilteredSelected();
    return selected && !all;
  }

  onCancel() {
    this.dialogRef.close();
  }

  onConfirm() {
    const selected = Array.from(this.selectedUserIds());
    if (selected.length > 0) {
      this.dialogRef.close(selected);
    }
  }
}
